import { ArrowRight } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import type { Remark } from '@/types/api';
import { normSeverity } from '@/types/api';

interface Props {
  before: Remark;
  after: Remark;
  onClose: () => void;
}

const SEV_COLOR: Record<string, string> = { critical: '#EF4444', high: '#F59E0B', medium: '#6B7280', low: '#9CA3AF' };
const SEV_LABEL: Record<string, string> = { critical: 'Критическое', high: 'Высокое', medium: 'Среднее', low: 'Низкое' };

/** Side-by-side diff между исходным и повторным замечанием. */
export function DiffViewModal({ before, after, onClose }: Props) {
  const sevBefore = normSeverity(before.severity);
  const sevAfter  = normSeverity(after.severity);
  const escalated = sevWeight(sevAfter) > sevWeight(sevBefore);

  return (
    <Modal
      title="Сравнение версий замечания"
      onClose={onClose}
      maxWidth={920}
      footer={<Button variant="primary" onClick={onClose}>Закрыть</Button>}
    >
      <div className="flex items-center justify-center mb-4" style={{ gap: 16 }}>
        <Pill text={`v1 · ${before.id.slice(0, 8)}`} />
        <ArrowRight size={20} stroke="#6B7280" />
        <Pill text={`v2 · ${after.id.slice(0, 8)}`} bold />
        {escalated && (
          <span className="rounded-pill" style={{ padding: '4px 10px', fontSize: 12, fontWeight: 600, background: 'rgba(239,68,68,.1)', color: '#EF4444' }}>
            эскалация: {SEV_LABEL[sevBefore]} → {SEV_LABEL[sevAfter]}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2" style={{ gap: 16 }}>
        <Column title="Исходное (v1)" remark={before} sev={sevBefore} />
        <Column title="Повторное (v2)" remark={after}  sev={sevAfter}  highlight={escalated} />
      </div>

      <div className="border-t border-border mt-5 pt-4">
        <div className="text-muted uppercase tracking-wider" style={{ fontSize: 11, fontWeight: 600 }}>Изменения</div>
        <ul className="mt-2" style={{ paddingLeft: 18, fontSize: 13, color: '#0A1628', lineHeight: 1.6 }}>
          {sevWeight(sevAfter) !== sevWeight(sevBefore) && (
            <li>Важность: <span className="text-muted">{SEV_LABEL[sevBefore]}</span> → <span style={{ color: SEV_COLOR[sevAfter], fontWeight: 600 }}>{SEV_LABEL[sevAfter]}</span></li>
          )}
          {before.specialist_role !== after.specialist_role && (
            <li>Эксперт: <span className="text-muted">{before.specialist_role || 'не назначен'}</span> → <span className="text-ink" style={{ fontWeight: 500 }}>{after.specialist_role || 'не назначен'}</span></li>
          )}
          <li>Длина текста: {before.body.length} → {after.body.length} символов</li>
          {before.cited_norms && after.cited_norms && before.cited_norms.length !== after.cited_norms.length && (
            <li>Цитат норм: {before.cited_norms.length} → {after.cited_norms.length}</li>
          )}
        </ul>
      </div>
    </Modal>
  );
}

function Column({ title, remark, sev, highlight }: { title: string; remark: Remark; sev: string; highlight?: boolean }) {
  return (
    <div className="rounded-card" style={{
      padding: 16,
      background: highlight ? 'rgba(245,158,11,.05)' : '#F3F4F6',
      border: highlight ? '1px solid rgba(245,158,11,.3)' : '1px solid #E5E7EB',
    }}>
      <div className="text-muted uppercase tracking-wider mb-2" style={{ fontSize: 11, fontWeight: 600 }}>{title}</div>
      <div className="flex items-center mb-3" style={{ gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: SEV_COLOR[sev] }}>{SEV_LABEL[sev]}</span>
        {remark.section_code && <Pill text={`Раздел ${remark.section_code}`} />}
        {remark.specialist_role && <Pill text={remark.specialist_role} />}
      </div>
      <p className="text-ink" style={{ fontSize: 14, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{remark.body}</p>
      {remark.created_at && (
        <p className="text-muted tabular mt-3" style={{ fontSize: 11 }}>{new Date(remark.created_at).toLocaleString('ru-RU')}</p>
      )}
    </div>
  );
}

function Pill({ text, bold }: { text: string; bold?: boolean }) {
  return (
    <span className="rounded-pill bg-white" style={{ padding: '2px 10px', fontSize: 11, fontWeight: bold ? 600 : 500, color: '#0A1628', border: '1px solid #E5E7EB' }}>{text}</span>
  );
}

function sevWeight(s: string): number {
  const w: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };
  return w[s] || 0;
}
